import React, { createContext, useContext, useState, useCallback } from 'react';
import AddAccountNewModal from '@/components/AddAccountNew/AddAccountNewModal';
import { EventNameSource } from '@/shared/event-types';

/**
 * 平台类型定义
 * 对应 Step1 中的 PLATFORMS 的 key 值
 */
export type PlatformKey =
  | 'ONLYFANS'
  | 'TIKTOK'
  | 'X'
  | 'INSTAGRAM'
  | 'PATREON'
  | 'CHATURBATE'
  | 'FANSLY'
  | 'FANVUE'
  | 'MANYVIDS'
  | 'REDDIT'
  | 'PORNHUB'
  | 'FANSONE'
  | 'OTHER';

/**
 * 打开弹框的配置选项
 */
export interface OpenAddAccountModalOptions {
  /** 页面来源，用于埋点追踪 */
  pageSource?: EventNameSource;
  /** 初始平台，如果指定则直接跳到第二步 */
  initialPlatform?: PlatformKey;
  /** 默认选中的平台，停留在第一步并高亮该平台，用户仍可改选 */
  defaultPlatform?: PlatformKey;
  /** 文案变体，'shadowban' 时使用检测泄露与假账号的专属文案 */
  copyVariant?: 'default' | 'shadowban';
  /** 添加成功后的回调函数 */
  onSuccess?: () => void;
}

interface AddAccountModalContextType {
  /**
   * 打开添加账号弹框
   * @param options 配置选项，支持 pageSource 和 initialPlatform
   */
  openAddAccountModal: (options?: OpenAddAccountModalOptions | EventNameSource) => void;
  /**
   * 关闭添加账号弹框
   */
  closeAddAccountModal: () => void;
  /**
   * 弹框是否打开
   */
  isOpen: boolean;
}

const AddAccountModalContext = createContext<AddAccountModalContextType>({
  openAddAccountModal: () => { },
  closeAddAccountModal: () => { },
  isOpen: false,
});

interface AddAccountModalProviderProps {
  children: React.ReactNode;
}

/**
 * 添加账号弹框的全局 Provider
 *
 * 使用方式：
 * 1. 在 _app.tsx 中包裹整个应用
 * 2. 在任意组件中使用 useAddAccountModal() 获取 openAddAccountModal 方法
 *
 * @example
 * ```tsx
 * const { openAddAccountModal } = useAddAccountModal();
 *
 * // 方式1：从第一步开始（选择平台）
 * openAddAccountModal(EventNameSource.home);
 *
 * // 方式2：直接跳到第二步（指定平台）
 * openAddAccountModal({
 *   pageSource: EventNameSource.dashboard,
 *   initialPlatform: 'INSTAGRAM'
 * });
 *
 * // 方式2b：停留在第一步，但预先选中某个平台（用户可改选）
 * openAddAccountModal({
 *   pageSource: EventNameSource.home,
 *   defaultPlatform: 'ONLYFANS'
 * });
 *
 * // 方式3：添加成功后执行回调
 * openAddAccountModal({
 *   pageSource: EventNameSource.dashboard,
 *   onSuccess: () => {
 *     console.log('账号添加成功！');
 *     // 执行其他逻辑...
 *   }
 * });
 * ```
 */
export const AddAccountModalProvider: React.FC<AddAccountModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pageSource, setPageSource] = useState<EventNameSource>(EventNameSource.home);
  const [initialPlatform, setInitialPlatform] = useState<PlatformKey | undefined>(undefined);
  const [defaultPlatform, setDefaultPlatform] = useState<PlatformKey | undefined>(undefined);
  const [copyVariant, setCopyVariant] = useState<'default' | 'shadowban'>('default');
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);

  const openAddAccountModal = useCallback((options?: OpenAddAccountModalOptions | EventNameSource) => {
    // 兼容旧的调用方式：直接传 EventNameSource
    if (typeof options === 'string') {
      setPageSource(options);
      setInitialPlatform(undefined);
      setDefaultPlatform(undefined);
      setCopyVariant('default');
      setOnSuccessCallback(undefined);
    } else if (options) {
      setPageSource(options.pageSource ?? EventNameSource.home);
      setInitialPlatform(options.initialPlatform);
      setDefaultPlatform(options.defaultPlatform);
      setCopyVariant(options.copyVariant ?? 'default');
      // 使用函数形式设置回调，避免 React 将函数当作 updater 执行
      setOnSuccessCallback(() => options.onSuccess);
    } else {
      setPageSource(EventNameSource.home);
      setInitialPlatform(undefined);
      setDefaultPlatform(undefined);
      setCopyVariant('default');
      setOnSuccessCallback(undefined);
    }
    setIsOpen(true);
  }, []);

  const closeAddAccountModal = useCallback(() => {
    setIsOpen(false);
    // 关闭后重置 initialPlatform / defaultPlatform，避免下次打开时还保留
    // 注意：不在这里清空 onSuccessCallback，避免竞态条件导致回调丢失
    setInitialPlatform(undefined);
    setDefaultPlatform(undefined);
    setCopyVariant('default');
  }, []);

  const handleSuccess = useCallback(() => {
    // 先保存回调引用，防止被清空
    const callback = onSuccessCallback;
    // 先关闭弹框
    closeAddAccountModal();
    // 执行回调
    callback?.();
    // 执行完回调后再清空，确保下次打开时不会残留
    setOnSuccessCallback(undefined);
  }, [onSuccessCallback, closeAddAccountModal]);

  const value: AddAccountModalContextType = {
    openAddAccountModal,
    closeAddAccountModal,
    isOpen,
  };

  return (
    <AddAccountModalContext.Provider value={value}>
      {children}
      {isOpen && (
        <AddAccountNewModal
          open={isOpen}
          pageSource={pageSource}
          onCancel={closeAddAccountModal}
          initialPlatform={initialPlatform}
          defaultPlatform={defaultPlatform}
          copyVariant={copyVariant}
          onSuccess={onSuccessCallback ? handleSuccess : undefined}
        />
      )}
    </AddAccountModalContext.Provider>
  );
};

/**
 * 获取添加账号弹框的控制方法
 *
 * @example
 * ```tsx
 * const { openAddAccountModal } = useAddAccountModal();
 *
 * // 从第一步开始
 * <button onClick={() => openAddAccountModal(EventNameSource.dashboard)}>
 *   添加账号
 * </button>
 *
 * // 直接跳到第二步（指定平台）
 * <button onClick={() => openAddAccountModal({
 *   pageSource: EventNameSource.dashboard,
 *   initialPlatform: 'TIKTOK'
 * })}>
 *   添加 TikTok 账号
 * </button>
 * ```
 */
export const useAddAccountModal = () => useContext(AddAccountModalContext);

export default AddAccountModalProvider;
